import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Box,
  Paper,
} from "@mui/material";
import "./DeviceDiagnostics.css";
import { Fragment } from "react";
import MuiDatePicker from "../../Common/FormInputs/MuiDatePicker";
import AppButton from "../../Common/Button/Button";
import EmptyState from "../../Common/EmptyState/EmptyState";
import IncrementalPagination from "../../Common/DataTable/IncrementalPagination";
import DisplayDeviceDiagnosticsData from "./DisplayDeviceDiagnosticsData";
import {
  deviceDiagnosticsPageSize,
  startDateLimit,
} from "./DeviceDiagnosticsConstants";
import { format } from "date-fns";

const DeviceDiagnostics = ({
  setDialog,
  setDialogContents,
  deviceDetails,
  getDeviceDiagnosticsData,
  diagnosticData,
  downloadDeviceDiagnosticReport,
}) => {
  const [deviceDiagnosticsData, setDeviceDiagnosticsData] = useState([]);
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeviceDiagnosticsDataList, setShowDeviceDiagnosticsDataList] =
    useState(false);
  const [isDataDownloading, setIsDataDownloading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const getDefaultDateRange = () => {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    return { startOfDay, now };
  };

  const handleDiagnosticsPageChange = (newPage) => {
    if (newPage === pageIndex) return;
    setPageIndex(newPage);
    fetchDeviceDiagnosticsData(newPage);
  };

  const handleDiagnosticsPageSizeChange = () => {
    // Page size is fixed via deviceDiagnosticsPageSize (API contract).
  };

  const formateDate = (dateFilter) => {
    if (dateFilter) {
      return dateFilter.getTime();
    }
  };

  const fetchDeviceDiagnosticsData = (
    page = pageIndex,
    fromTs = formateDate(startDateFilter),
    toTs = formateDate(endDateFilter)
  ) => {
    setIsLoading(true);
    getDeviceDiagnosticsData(
      {
        deviceId: deviceDetails?.deviceId ?? "",
        fromTs,
        toTs,
        index: page,
        size: deviceDiagnosticsPageSize,
      },
      () => {
        setIsLoading(false);
        setShowDeviceDiagnosticsDataList(true);
      }
    );
  };

  const reset = () => {
    const { startOfDay, now } = getDefaultDateRange();
    setStartDateFilter(startOfDay);
    setEndDateFilter(now);
    setPageIndex(0);
    fetchDeviceDiagnosticsData(
      0,
      formateDate(startOfDay),
      formateDate(now)
    );
  };

  const filter = () => {
    setPageIndex(0);
    fetchDeviceDiagnosticsData(0);
  };

  const handleStartDateChange = (date) => {
    setStartDateFilter(date);
    setEndDateFilter(null);
    setShowDeviceDiagnosticsDataList(false);
  };

  const handleEndDateChange = (date) => {
    setEndDateFilter(date);
    setShowDeviceDiagnosticsDataList(false);
  };

  function handleDownloadDiagnosticsDeviceData() {
    setIsDataDownloading(true);
    downloadDeviceDiagnosticReport(
      {
        fromTs: formateDate(startDateFilter),
        toTs: formateDate(endDateFilter),
        deviceId: deviceDetails.deviceId,
        printableStartDate: `${format(startDateFilter, "dd MMM yyyy")}-${format(endDateFilter, "dd MMM yyyy")}`,
      },
      () => {
        setIsDataDownloading(false);
      }
    );
  }

  const minStartDate = (() => {
    const futureDate = new Date();
    return new Date(
      futureDate?.setDate(futureDate?.getDate() - startDateLimit)
    );
  })();

  useEffect(() => {
    const { startOfDay, now } = getDefaultDateRange();
    setStartDateFilter(startOfDay);
    setEndDateFilter(now);
    setIsLoading(true);
    getDeviceDiagnosticsData(
      {
        deviceId: deviceDetails?.deviceId,
        fromTs: formateDate(startOfDay),
        toTs: formateDate(now),
        index: 0,
        size: deviceDiagnosticsPageSize,
      },
      () => {
        setIsLoading(false);
        setShowDeviceDiagnosticsDataList(true);
      }
    );
    setPageIndex(0);
  }, [deviceDetails?.deviceId, getDeviceDiagnosticsData]);

  useEffect(() => {
    setDeviceDiagnosticsData(diagnosticData);
  }, [diagnosticData]);

  return (
    <Fragment>
      <Box sx={{ width: "100%", minWidth: 0 }}>
        <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, overflow: "hidden" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "minmax(0, 220px) minmax(0, 220px) auto",
              },
              gap: 2,
              alignItems: "center",
            }}
          >
            <Box sx={{ minWidth: 0, width: "100%" }}>
              <MuiDatePicker
                label="Start Date"
                value={startDateFilter}
                onChange={handleStartDateChange}
                minDate={minStartDate}
                maxDate={new Date()}
                sx={{ width: "100%" }}
              />
            </Box>
            <Box sx={{ minWidth: 0, width: "100%" }}>
              <MuiDatePicker
                label="End Date"
                value={endDateFilter}
                onChange={handleEndDateChange}
                minDate={startDateFilter}
                maxDate={new Date()}
                sx={{ width: "100%" }}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <AppButton
                  variant="primary"
                  size="medium"
                  onClick={filter}
                  disabled={startDateFilter === null || endDateFilter === null}
                  sx={{ height: 40 }}
                >
                  Filter
                </AppButton>
                <AppButton variant="ghost" size="medium" onClick={reset} sx={{ height: 40 }}>
                  Reset
                </AppButton>
                <AppButton
                  variant="primary"
                  size="medium"
                  onClick={handleDownloadDiagnosticsDeviceData}
                  disabled={
                    isDataDownloading ||
                    startDateFilter === null ||
                    endDateFilter === null
                  }
                  sx={{ height: 40 }}
                >
                  {isDataDownloading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: "inherit" }} />
                      Download
                    </Box>
                  ) : (
                    "Download"
                  )}
                </AppButton>
              </Box>
            </Box>
          </Box>
        </Paper>

      {isLoading && (
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            height: "20vh",
            mt: 2,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#AFB42B" }} />
        </Box>
      )}

      {!isLoading && showDeviceDiagnosticsDataList && (
        <Paper
          sx={{
            p: 2,
            boxShadow: 2,
            borderRadius: 1,
            mt: 2,
            mb: 2,
            overflow: "hidden",
          }}
        >
          {!Array.isArray(deviceDiagnosticsData) || deviceDiagnosticsData.length === 0 ? (
            <EmptyState
              message="No Data found"
              sx={{ bgcolor: "transparent", p: 0 }}
            />
          ) : (
            <DisplayDeviceDiagnosticsData
              setDialog={setDialog}
              setDialogContents={setDialogContents}
              deviceCategory={deviceDetails.category}
              deviceDiagnosticsData={deviceDiagnosticsData}
            />
          )}
          {Array.isArray(deviceDiagnosticsData) && deviceDiagnosticsData.length > 0 && (
            <IncrementalPagination
              page={pageIndex}
              pageSize={deviceDiagnosticsPageSize}
              dataLength={deviceDiagnosticsData.length}
              onPageChange={handleDiagnosticsPageChange}
              onPageSizeChange={handleDiagnosticsPageSizeChange}
              pageSizeOptions={[deviceDiagnosticsPageSize]}
              isLastPage={
                deviceDiagnosticsData.length < deviceDiagnosticsPageSize
              }
              sx={{ m: 0, mt: 1.5, px: 0 }}
            />
          )}
        </Paper>
      )}
      </Box>
    </Fragment>
  );
};

export default DeviceDiagnostics;
